// Cross-module weekly/monthly markdown report. Each section is a pure
// builder over the rows returned from one supabase query — no DOM,
// no formatting hooks, no DB calls. The page fires off the queries
// in parallel and hands the arrays in here.
//
// Output is ONE markdown string, sections labelled per locale.

const LABELS = {
  ar: {
    title: 'تقرير الفترة',
    rangeLabel: 'الفترة',
    generatedAt: 'تاريخ التقرير',
    overview: 'نظرة عامة',
    workout: 'التمارين',
    sessions: 'الجلسات',
    totalVolume: 'إجمالي الحمل',
    workSets: 'سيتات (بدون إحماء)',
    cardio: 'كارديو',
    activeDays: 'أيام نشاط',
    avg: 'المتوسط',
    none: 'لا يوجد',
    phoenix: 'فينيكس',
    weight: 'الوزن',
    weightStart: 'بداية الفترة',
    weightEnd: 'نهاية الفترة',
    weightDelta: 'الفرق',
    water: 'المياه',
    avgDaily: 'متوسط يومي',
    hitRate: 'نسبة تحقيق الهدف',
    streak: 'السلسلة',
    meals: 'الوجبات',
    mealsLogged: 'وجبات مسجّلة',
    avgKcal: 'متوسط السعرات اليومي',
    avgProtein: 'متوسط البروتين',
    health: 'الملف الصحي',
    medAdherence: 'الالتزام بالأدوية',
    suppAdherence: 'الالتزام بالفيتامينات',
    vitals: 'القياسات الحيوية',
    latest: 'آخر قياس',
    finance: 'الماليات',
    balanceChange: 'تغير الرصيد',
    balanceStart: 'بداية',
    balanceEnd: 'نهاية',
    achieved: 'أهداف محققة',
    days: 'يوم',
    minutes: 'دقيقة',
    item: 'البند',
    metric: 'القيمة',
    value: 'الرقم',
    doses: 'جرعات',
    name: 'الاسم',
    taken: 'تم تناولها',
    expected: 'المتوقع',
    rate: 'النسبة',
  },
  en: {
    title: 'Period Report',
    rangeLabel: 'Range',
    generatedAt: 'Generated on',
    overview: 'Overview',
    workout: 'Workout',
    sessions: 'Sessions',
    totalVolume: 'Total volume',
    workSets: 'Work sets',
    cardio: 'Cardio',
    activeDays: 'Active days',
    avg: 'Avg',
    none: 'None',
    phoenix: 'Phoenix',
    weight: 'Weight',
    weightStart: 'Start',
    weightEnd: 'End',
    weightDelta: 'Δ',
    water: 'Water',
    avgDaily: 'Avg daily',
    hitRate: 'Hit rate',
    streak: 'Streak',
    meals: 'Meals',
    mealsLogged: 'Meals logged',
    avgKcal: 'Avg daily kcal',
    avgProtein: 'Avg protein',
    health: 'Health',
    medAdherence: 'Medication adherence',
    suppAdherence: 'Supplement adherence',
    vitals: 'Vitals',
    latest: 'Latest',
    finance: 'Finance',
    balanceChange: 'Balance change',
    balanceStart: 'Start',
    balanceEnd: 'End',
    achieved: 'Achieved targets',
    days: 'days',
    minutes: 'min',
    item: 'Item',
    metric: 'Metric',
    value: 'Value',
    doses: 'doses',
    name: 'Name',
    taken: 'Taken',
    expected: 'Expected',
    rate: 'Rate',
  },
}

function num(n, decimals = 0) {
  if (n == null || isNaN(n)) return '—'
  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

function inRange(date, startIso, endIso) {
  if (!date) return false
  const d = date.slice(0, 10)
  return d >= startIso && d <= endIso
}

function daysBetweenIso(startIso, endIso) {
  const a = new Date(`${startIso}T00:00:00Z`)
  const b = new Date(`${endIso}T00:00:00Z`)
  return Math.max(1, Math.round((b - a) / 86400000) + 1)
}

// ---- Per-module section builders -----------------------------------------

function workoutSection(sessions, startIso, endIso, L) {
  const filtered = sessions.filter((s) => inRange(s.date, startIso, endIso))
  if (filtered.length === 0) {
    return [`### ${L.workout}`, '', `_${L.none}_`, ''].join('\n')
  }
  let workSets = 0
  let totalVolume = 0
  let cardioMin = 0
  const activeDays = new Set()
  for (const s of filtered) {
    activeDays.add(s.date)
    for (const log of s.exercise_logs || []) {
      for (const set of log.sets || []) {
        if (set.is_warmup) continue
        workSets += 1
        totalVolume += (Number(set.weight_kg) || 0) * (Number(set.reps) || 0)
      }
    }
    for (const c of s.cardio_logs || []) {
      cardioMin += Number(c.duration_min) || 0
    }
  }
  return [
    `### ${L.workout}`,
    '',
    `| ${L.metric} | ${L.value} |`,
    '|---|---|',
    `| ${L.sessions} | ${filtered.length} |`,
    `| ${L.activeDays} | ${activeDays.size} |`,
    `| ${L.workSets} | ${num(workSets)} |`,
    `| ${L.totalVolume} | ${num(totalVolume)} kg |`,
    `| ${L.cardio} | ${num(cardioMin)} ${L.minutes} |`,
    '',
  ].join('\n')
}

function phoenixSection({ weights, waters, meals, waterGoalMl }, startIso, endIso, L) {
  const days = daysBetweenIso(startIso, endIso)

  // Weight: start vs end (closest entry to each endpoint within range)
  const inrangeWeights = weights
    .filter((w) => inRange(w.date, startIso, endIso))
    .sort((a, b) => (a.date < b.date ? -1 : 1))
  const wStart = inrangeWeights[0]
  const wEnd = inrangeWeights[inrangeWeights.length - 1]

  // Water: sum per day, then average + hit rate
  const waterByDay = new Map()
  for (const w of waters) {
    if (!inRange(w.date, startIso, endIso)) continue
    waterByDay.set(w.date, (waterByDay.get(w.date) || 0) + (Number(w.amount_ml) || 0))
  }
  const totalWaterMl = Array.from(waterByDay.values()).reduce((s, v) => s + v, 0)
  const avgWaterMl = waterByDay.size > 0 ? totalWaterMl / waterByDay.size : 0
  const goalMl = Number(waterGoalMl) || 3000
  const daysHitGoal = Array.from(waterByDay.values()).filter((v) => v >= goalMl).length
  const hitRate = waterByDay.size > 0 ? (daysHitGoal / waterByDay.size) * 100 : 0

  // Meals
  const inrangeMeals = meals.filter((m) => inRange(m.date, startIso, endIso))
  const totalKcal = inrangeMeals.reduce((s, m) => s + (Number(m.kcal) || 0), 0)
  const totalProtein = inrangeMeals.reduce((s, m) => s + (Number(m.protein_g) || 0), 0)
  const mealDays = new Set(inrangeMeals.map((m) => m.date))
  const avgKcal = mealDays.size > 0 ? totalKcal / mealDays.size : 0
  const avgProtein = mealDays.size > 0 ? totalProtein / mealDays.size : 0

  const lines = [`### ${L.phoenix}`, '']
  lines.push('')
  lines.push(`**${L.weight}**  `)
  if (wStart && wEnd) {
    const delta = (Number(wEnd.weight) || 0) - (Number(wStart.weight) || 0)
    lines.push(
      `${L.weightStart}: ${num(wStart.weight, 1)} kg · ${L.weightEnd}: ${num(wEnd.weight, 1)} kg · ${L.weightDelta}: ${delta >= 0 ? '+' : ''}${num(delta, 1)} kg`,
    )
  } else {
    lines.push(`_${L.none}_`)
  }
  lines.push('')

  lines.push(`**${L.water}**  `)
  if (waterByDay.size > 0) {
    lines.push(
      `${L.avgDaily}: ${num(avgWaterMl)} ml · ${L.hitRate}: ${num(hitRate)}% (${daysHitGoal} / ${waterByDay.size} ${L.days})`,
    )
  } else {
    lines.push(`_${L.none}_`)
  }
  lines.push('')

  lines.push(`**${L.meals}**  `)
  if (inrangeMeals.length > 0) {
    lines.push(
      `${L.mealsLogged}: ${inrangeMeals.length} · ${L.avgKcal}: ${num(avgKcal)} · ${L.avgProtein}: ${num(avgProtein)}g`,
    )
  } else {
    lines.push(`_${L.none}_`)
  }
  lines.push('')

  return lines.join('\n')
}

function adherenceFor(items, intakeLog, startIso, endIso) {
  // items: array of { id, name, schedule_times, started_at, stopped_at }
  const days = daysBetweenIso(startIso, endIso)
  return items
    .filter((it) => Array.isArray(it.schedule_times) && it.schedule_times.length > 0)
    .map((it) => {
      // Day overlap: from max(item start, range start) to min(item stop?, range end)
      const itemStart = (it.started_at && it.started_at > startIso) ? it.started_at : startIso
      const itemEnd = (it.stopped_at && it.stopped_at < endIso) ? it.stopped_at : endIso
      const overlapDays = itemEnd < itemStart ? 0 : daysBetweenIso(itemStart, itemEnd)
      const expected = overlapDays * it.schedule_times.length
      const taken = intakeLog.filter(
        (l) => l.item_id === it.id && inRange(l.date, startIso, endIso),
      ).length
      const rate = expected > 0 ? (taken / expected) * 100 : 0
      return { name: it.name, taken, expected, rate }
    })
}

function healthSection({ medications, supplements, intakeLog, vitalsByKind }, startIso, endIso, L) {
  const out = [`### ${L.health}`, '']

  const meds = adherenceFor(medications, intakeLog.filter((l) => l.item_kind === 'medication'), startIso, endIso)
  const supps = adherenceFor(supplements, intakeLog.filter((l) => l.item_kind === 'supplement'), startIso, endIso)

  function adherenceTable(label, rows) {
    if (rows.length === 0) return [`**${label}**  `, `_${L.none}_`, ''].join('\n')
    const lines = [`**${label}**  `, '', `| ${L.name} | ${L.taken} / ${L.expected} | ${L.rate} |`, '|---|---|---|']
    for (const r of rows) {
      lines.push(`| ${r.name} | ${r.taken} / ${r.expected} | ${num(r.rate)}% |`)
    }
    lines.push('')
    return lines.join('\n')
  }

  out.push(adherenceTable(L.medAdherence, meds))
  out.push(adherenceTable(L.suppAdherence, supps))

  if (vitalsByKind && Object.keys(vitalsByKind).length > 0) {
    out.push(`**${L.vitals}**  `)
    out.push('')
    for (const kind of Object.keys(vitalsByKind)) {
      const arr = vitalsByKind[kind]
      const latest = arr.filter((v) => inRange(v.measured_at, startIso, endIso)).slice(-1)[0]
      if (!latest) continue
      let value = ''
      if (kind === 'blood_pressure') value = `${latest.systolic}/${latest.diastolic}`
      else if (kind === 'heart_rate') value = `${latest.bpm} bpm`
      else if (kind === 'glucose') value = `${latest.glucose_mg_dl} mg/dL`
      out.push(`- **${kind}** — ${L.latest}: ${value} (${(latest.measured_at || '').slice(0, 10)})`)
    }
    out.push('')
  }

  return out.join('\n')
}

function financeSection({ balanceEntries, targets, incomeSources, fixedExpenses, installments, variableEstimate }, startIso, endIso, L) {
  const out = [`### ${L.finance}`, '']

  // Balance start/end
  const inrange = balanceEntries
    .filter((b) => inRange(b.as_of_date, startIso, endIso))
    .sort((a, b) => (a.as_of_date < b.as_of_date ? -1 : 1))
  const bStart = inrange[0]
  const bEnd = inrange[inrange.length - 1]
  out.push(`**${L.balanceChange}**  `)
  if (bStart && bEnd) {
    const delta = Number(bEnd.amount) - Number(bStart.amount)
    out.push(
      `${L.balanceStart}: ${num(bStart.amount)} SAR (${bStart.as_of_date}) · ${L.balanceEnd}: ${num(bEnd.amount)} SAR (${bEnd.as_of_date}) · ${L.weightDelta}: ${delta >= 0 ? '+' : ''}${num(delta)} SAR`,
    )
  } else {
    out.push(`_${L.none}_`)
  }
  out.push('')

  // Achieved targets in range
  const achieved = (targets || []).filter(
    (t) => t.status === 'achieved' && t.achieved_at && inRange(t.achieved_at, startIso, endIso),
  )
  out.push(`**${L.achieved}**  `)
  if (achieved.length > 0) {
    for (const t of achieved) {
      out.push(`- ${t.name} (${t.achieved_at})`)
    }
  } else {
    out.push(`_${L.none}_`)
  }
  out.push('')

  return out.join('\n')
}

// ---- Top-level builder ---------------------------------------------------

export function buildPeriodReport({
  startIso,
  endIso,
  language = 'en',
  data,
}) {
  const L = LABELS[language === 'ar' ? 'ar' : 'en']
  const today = new Date().toLocaleDateString('en-CA')
  const out = [
    `# ${L.title}`,
    '',
    `**${L.rangeLabel}**: ${startIso} → ${endIso}  `,
    `**${L.generatedAt}**: ${today}`,
    '',
    '---',
    '',
  ]
  out.push(workoutSection(data.sessions || [], startIso, endIso, L))
  out.push('---')
  out.push('')
  out.push(phoenixSection({
    weights: data.weights || [],
    waters: data.waters || [],
    meals: data.meals || [],
    waterGoalMl: data.waterGoalMl,
  }, startIso, endIso, L))
  out.push('---')
  out.push('')
  out.push(healthSection({
    medications: data.medications || [],
    supplements: data.supplements || [],
    intakeLog: data.intakeLog || [],
    vitalsByKind: data.vitalsByKind || {},
  }, startIso, endIso, L))
  out.push('---')
  out.push('')
  out.push(financeSection({
    balanceEntries: data.balanceEntries || [],
    targets: data.targets || [],
  }, startIso, endIso, L))
  return out.join('\n')
}

export function suggestedReportFilename(language, startIso, endIso) {
  const base = language === 'ar' ? 'تقرير' : 'period-report'
  return `${base}-${startIso}_${endIso}.md`
}

// Convenience presets: today's locale-aware "this week" (Mon-anchored)
// and "this month" so the picker can default to something reasonable.
export function presetRange(preset, today = new Date()) {
  const t = new Date(today)
  t.setHours(0, 0, 0, 0)
  if (preset === 'last7') {
    const end = new Date(t)
    const start = new Date(t)
    start.setDate(t.getDate() - 6)
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) }
  }
  if (preset === 'last30') {
    const end = new Date(t)
    const start = new Date(t)
    start.setDate(t.getDate() - 29)
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) }
  }
  if (preset === 'thisMonth') {
    const start = new Date(t.getFullYear(), t.getMonth(), 1)
    const end = new Date(t)
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) }
  }
  // 'thisWeek' (Mon → today)
  const day = t.getDay() // Sun = 0
  const offset = (day + 6) % 7 // Mon = 0
  const start = new Date(t)
  start.setDate(t.getDate() - offset)
  return { start: start.toISOString().slice(0, 10), end: t.toISOString().slice(0, 10) }
}
